import { useState, useEffect, useMemo, useRef } from "react";
import { getMenuCached } from "../../utils/menuCache";
import { useRouter } from "next/router";
import Link from "next/link";
import Image from "next/image";
import { FiMenu, FiX, FiChevronDown } from "react-icons/fi";
import { AnimatePresence, motion } from "framer-motion";
import { useLanguage } from "../../context/LanguageContext";
import { useTranslation } from "next-i18next";
import LanguageSwitcher from "./LanguageSwitcher";
import MegaMenu from "./MegaMenu";
import MobileDrillMenu from "./MobileDrillMenu";
import {
  fetchMenuServicesData,
  fetchMenuIndustriesData,
} from "../http/commonApi";

const baseNavLinks = [
  {
    labelKey: "header.about_us",
    path: "/about-prism-digital-marketing-agency",
  },
  { labelKey: "header.services", path: "/services", hasChildren: true },
  { labelKey: "header.industries", path: "/industry", hasChildren: true },
  { labelKey: "header.clients", path: "/our-clients" },
  { labelKey: "header.caseStudies", path: "/case-study" },
  { labelKey: "header.portfolio", path: "/portfolio" },
  { labelKey: "header.testimonials", path: "/digital-marketing-reviews" },
  { labelKey: "header.blogs", path: "/blog" },
  { labelKey: "header.contact", path: "/contact-digital-marketing-agency" },
];

const toolsMenuItems = [
  { label: "AI Readiness Checker", path: "/ai-readiness-checker" },
  { label: "SEO Audit", path: "/free-seo-audit-tool" },
];

const Header = ({ serviceDetails }) => {
  const router = useRouter();
  const { locale } = useLanguage();
  const { t } = useTranslation("header");

  const [activeMenuKey, setActiveMenuKey] = useState(null);
  const [isToolsOpen, setIsToolsOpen] = useState(false);
  const toolsRef = useRef(null);

  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [mobileDropdownOpen, setMobileDropdownOpen] = useState(null);
  const [mobileChildOpen, setMobileChildOpen] = useState(null);
  const [mobileGrandchildOpen, setMobileGrandchildOpen] = useState(null);
  const [isMobileToolsOpen, setIsMobileToolsOpen] = useState(false);

  const [isScrolled, setIsScrolled] = useState(false);

  const [serviceDropdown, setServiceDropdown] = useState([]);
  const [industriesDropdown, setIndustriesDropdown] = useState([]);

  const [servicesLoading, setServicesLoading] = useState(true);
  const [industriesLoading, setIndustriesLoading] = useState(true);

  const isServiceActive = activeMenuKey === "header.services";
  const isIndustryActive = activeMenuKey === "header.industries";

  const megaMenuData = useMemo(() => {
    if (isServiceActive) return serviceDropdown || [];
    if (isIndustryActive) return industriesDropdown || [];
    return [];
  }, [isServiceActive, isIndustryActive, serviceDropdown, industriesDropdown]);

  const megaMenuLoading =
    (isServiceActive && servicesLoading) ||
    (isIndustryActive && industriesLoading);

  // --- fetch services ---
  useEffect(() => {
    if (!locale) return;
    let alive = true;

    (async () => {
      try {
        setServicesLoading(true);
        const data = await getMenuCached(
          "services",
          locale,
          fetchMenuServicesData,
        );
        if (alive)
          setServiceDropdown(Array.isArray(data?.data) ? data?.data : []);
      } catch (e) {
        console.error("services menu error:", e);
        if (alive) setServiceDropdown([]);
      } finally {
        if (alive) setServicesLoading(false);
      }
    })();

    return () => {
      alive = false;
    };
  }, [locale]);

  // --- fetch industries ---
  useEffect(() => {
    if (!locale) return;
    let alive = true;

    (async () => {
      try {
        setIndustriesLoading(true);
        const data = await getMenuCached(
          "industries",
          locale,
          fetchMenuIndustriesData,
        );
        if (alive) setIndustriesDropdown(Array.isArray(data) ? data : []);
      } catch (e) {
        console.error("industries menu error:", e);
        if (alive) setIndustriesDropdown([]);
      } finally {
        if (alive) setIndustriesLoading(false);
      }
    })();

    return () => {
      alive = false;
    };
  }, [locale]);

  // warm cache
  useEffect(() => {
    if (!locale) return;

    const id = setTimeout(() => {
      getMenuCached("services", locale, fetchMenuServicesData).catch(() => {});
      getMenuCached("industries", locale, fetchMenuIndustriesData).catch(
        () => {},
      );
    }, 200);

    return () => clearTimeout(id);
  }, [locale]);

  // ✅ close megamenu on scroll — throttled via rAF, bails out if isScrolled
  // value hasn't actually changed, so we're not forcing a re-render of the
  // whole Header dozens of times per second while the user scrolls/flings.
  useEffect(() => {
    let ticking = false;

    const handleScroll = () => {
      if (ticking) return;
      ticking = true;

      window.requestAnimationFrame(() => {
        setIsScrolled((prev) => {
          const next = window.scrollY > 50;
          return prev === next ? prev : next;
        });
        setActiveMenuKey(null);
        setIsToolsOpen(false);
        ticking = false;
      });
    };

    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  // close tools dropdown on outside click
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (toolsRef.current && !toolsRef.current.contains(e.target)) {
        setIsToolsOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // close megamenu on route change
  useEffect(() => {
    const handleRouteChange = () => {
      setActiveMenuKey(null);
      setIsToolsOpen(false);
    };

    router.events.on("routeChangeStart", handleRouteChange);
    return () => {
      router.events.off("routeChangeStart", handleRouteChange);
    };
  }, [router]);

  // reset UI on locale change
  useEffect(() => {
    setActiveMenuKey(null);
    setMobileDropdownOpen(null);
    setMobileChildOpen(null);
    setMobileGrandchildOpen(null);
    setIsMobileMenuOpen(false);
    setIsToolsOpen(false);
    setIsMobileToolsOpen(false);
  }, [locale]);

  const rawPath = router.asPath.split("?")[0];
  const normalizedPath = rawPath.replace(/^\/(en|ar)/, "");
  const hideLangSwitcher =
    // normalizedPath.startsWith("/news") ||
    normalizedPath.startsWith("/podcasts") ||
    normalizedPath.startsWith("/ai-readiness");

  return (
    <>
      {/* top bar mobile */}
      <div className="flex flex-row items-center justify-between gap-4 px-6 py-4 bg-black border-b border-gray-300 border-solid lg:hidden">
        {!hideLangSwitcher && <LanguageSwitcher locale={locale} />}
        <button
          className="w-max text-[14px] bg-white text-black py-[8px] px-[20px] rounded-full cursor-pointer"
          onClick={() => router.push("/free-seo-audit-tool")}
        >
          {t("header.buttonText")}
        </button>
      </div>

      {/* main navbar */}
      <header
        className={`relative w-full bg-black sticky top-0 right-0 left-0 z-[99] py-2.5 flex flex-col box-border text-white transition-colors duration-300 px-[24px] md:px-[40px] lg:px-[30px] xl:px-[60px] ${
          isScrolled ? "shadow-lg" : ""
        }`}
      >
        <div className="w-full box-border max-w-[1600px] mx-auto">
          <div className="flex items-center justify-between w-full gap-4">
            {/* Logo */}
            <Image
              onClick={() => router.push("/")}
              className="h-[60px] w-[120px] object-cover cursor-pointer"
              src="/logo@2x.png"
              width={120}
              height={60}
              alt="prism digital marketing agency logo"
              priority
            />

            <nav className="box-border relative items-center justify-center hidden w-full gap-3 lg:flex xl:gap-4">
              {baseNavLinks.map(({ labelKey, path, hasChildren }) => {
                return (
                  <div
                    key={labelKey}
                    className="relative group/nav"
                    onMouseEnter={() => {
                      if (!hasChildren) {
                        setActiveMenuKey(null);
                        return;
                      }
                      setActiveMenuKey(labelKey);
                    }}
                  >
                    <Link href={path} className="text-white no-underline">
                      <div className="flex items-center gap-2 cursor-pointer text-[11px] xl:text-[14px] capitalize font-medium">
                        <span>{t(labelKey)}</span>
                        {hasChildren && (
                          <Image
                            src="/iconamoonarrowup2light@2x.png"
                            width={16}
                            height={16}
                            alt="prism digital marketing agency arrow dropdown"
                            className="w-4 h-4"
                          />
                        )}
                      </div>
                    </Link>
                  </div>
                );
              })}

              {/* ── Tools dropdown ── */}
              {locale !== "ar" && (
                <div
                  ref={toolsRef}
                  className="relative"
                  onMouseEnter={() => {
                    setActiveMenuKey(null);
                    setIsToolsOpen(true);
                  }}
                  onMouseLeave={() => setIsToolsOpen(false)}
                >
                  <button className="flex items-center gap-1.5 cursor-pointer text-[11px] xl:text-[14px] capitalize font-medium text-white bg-transparent border-none outline-none">
                    <span>Tools</span>
                    <FiChevronDown
                      className={`w-3.5 h-3.5 transition-transform duration-200 ${
                        isToolsOpen ? "rotate-180" : ""
                      }`}
                    />
                  </button>

                  <AnimatePresence>
                    {isToolsOpen && (
                      <motion.div
                        initial={{ opacity: 0, scaleY: 0 }}
                        animate={{ opacity: 1, scaleY: 1 }}
                        exit={{ opacity: 0, scaleY: 0 }}
                        transition={{ duration: 0.18, ease: "easeOut" }}
                        style={{
                          transformOrigin: "top",
                          background: "#000",
                          borderRadius: "16px",
                          border: "1px solid rgba(255,255,255,0.08)",
                          boxShadow:
                            "0 24px 60px rgba(0,0,0,0.6), 0 0 0 1px rgba(255,255,255,0.04) inset",
                        }}
                        className="absolute top-full left-1/2 -translate-x-1/2 mt-4 w-[280px] z-[999]"
                      >
                        <div className="px-3 py-3 flex flex-col gap-1">
                          {toolsMenuItems.map((item) => (
                            <Link
                              key={item.path}
                              href={item.path}
                              onClick={() => setIsToolsOpen(false)}
                              className="no-underline group/tool"
                            >
                              <div
                                className="flex items-center gap-3 px-2 py-1 rounded-xl transition-all duration-200 cursor-pointer"
                                onMouseEnter={(e) => {
                                  e.currentTarget.querySelector(
                                    "span",
                                  ).style.color = "rgba(255,255,255,0.6)";
                                }}
                                onMouseLeave={(e) => {
                                  e.currentTarget.querySelector(
                                    "span",
                                  ).style.color = "white";
                                }}
                              >
                                <span className="block text-[14px] xl:text-[16px] leading-[170%] font-semibold text-white capitalize">
                                  {item.label} (Free)
                                </span>
                              </div>
                            </Link>
                          ))}
                        </div>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </div>
              )}
              {/* ── /Tools dropdown ── */}
            </nav>

            {/* Right Side */}
            <div className="items-center hidden gap-4 lg:flex">
              {!hideLangSwitcher && <LanguageSwitcher locale={locale} />}
              <button
                className="w-max bg-white rounded-[50px] py-[8px] px-[20px] text-black text-[12px] xl:text-[14px] font-medium leading-[150%] cursor-pointer hover:bg-[#e72c46] hover:text-white transition duration-300 ease-in-out"
                onClick={() => router.push("/free-seo-audit-tool")}
              >
                {t("header.buttonText")}
              </button>
            </div>

            <button
              className="p-2 bg-transparent lg:hidden"
              onClick={() => setIsMobileMenuOpen((v) => !v)}
            >
              {isMobileMenuOpen ? (
                <FiX className="w-5 h-5 text-white bg-transparent" />
              ) : (
                <FiMenu className="w-5 h-5 text-white bg-transparent" />
              )}
            </button>
          </div>

          {/* MegaMenu Dropdown */}
          <AnimatePresence>
            {activeMenuKey && (
              <motion.div
                key={activeMenuKey}
                initial={{ height: 0, opacity: 0 }}
                animate={{ height: "auto", opacity: 1 }}
                exit={{ height: 0, opacity: 0 }}
                transition={{ duration: 0.25, ease: "easeInOut" }}
                className="absolute left-0 right-0 top-full bg-black text-white shadow-lg border-t border-white/50 z-[999]"
                style={{ maxHeight: "calc(100vh - 90px)" }}
              >
                <div className="max-w-[1600px] mx-auto px-[24px] md:px-[40px] lg:px-[30px] xl:px-[60px] py-3">
                  <MegaMenu
                    children={megaMenuData}
                    isService={isServiceActive}
                    onCloseMenu={() => setActiveMenuKey(null)}
                    loading={megaMenuLoading || megaMenuData.length === 0}
                  />
                </div>
              </motion.div>
            )}
          </AnimatePresence>

          {/* Mobile Menu */}
          {isMobileMenuOpen && (
            <div className="px-4 py-4 mt-4 text-white lg:hidden bg-black/90">
              {baseNavLinks.map(({ labelKey, path, hasChildren }) => {
                const isServiceLink = labelKey === "header.services";
                const isIndustryLink = labelKey === "header.industries";

                const list = isServiceLink
                  ? serviceDropdown
                  : isIndustryLink
                    ? industriesDropdown
                    : null;

                if (!hasChildren || !list?.length) {
                  return (
                    <Link
                      key={labelKey}
                      href={path}
                      className="flex items-center justify-between py-2 border-b border-gray-800 text-[14px] text-white no-underline"
                      onClick={() => setIsMobileMenuOpen(false)}
                    >
                      <span>{t(labelKey)}</span>
                    </Link>
                  );
                }

                return (
                  <MobileDrillMenu
                    key={labelKey}
                    title={t(labelKey)}
                    items={list}
                    isService={isServiceLink}
                    locale={locale}
                    onNavigate={() => setIsMobileMenuOpen(false)}
                  />
                );
              })}

              {/* ── Mobile Tools section ── */}
              {locale !== "ar" && (
                <MobileDrillMenu
                  title="Tools"
                  items={toolsMenuItems}
                  isService={false}
                  isTools={true}
                  locale={locale}
                  onNavigate={() => setIsMobileMenuOpen(false)}
                />
              )}
              {/* ── /Mobile Tools section ── */}
            </div>
          )}
        </div>
      </header>
    </>
  );
};

export default Header;
